import { Fragment, useEffect, useRef, useState } from 'react'
import { Color, Spacing } from '@opallabs/gem'
import { ThemeProvider } from '@opallabs/hooks'
import { kuiperClassNames, Loader } from '@opallabs/kuiper'
import { ContentApprovalStatus } from '@opallabs/kuiper/src/ApprovalStatus/helpers'
import { Popover, Button, DotLoader, WorkflowIcon } from '@opallabs/lapidary'
import { track } from '@opallabs/quanta'
import { Content } from '@opallabs/wormhole/types'
import { WorkflowLocation } from '../helpers/matchers'
import { findFirstCompletePhase } from '../helpers/phaseHelpers'
import PhaseList from '../PhaseList'
import { isFinalApprovalReached } from '../PhaseList/helpers/items'
import WorkflowPopoverFooter from './Footer'
import { getApprovalContent } from './Footer/helpers/approvalStatus'
import { getDeliveryContent } from './Footer/helpers/deliveryStatus'
import { Phase, User } from './Footer/types'
import WorkflowPopoverHeader from './Header'
import './styles.scss'

const statusMap = {
  approved: 'approval-approved',
  declined: 'approval-denied',
  denied: 'approval-denied',
  default: 'not-started',
  draft: 'not-started',
  for_approval: 'approval-pending',
  needs_revision: 'approval-denied',
  partial: 'approval-pending',
  pending: 'approval-pending',
  progress: 'approval-approved',
  published: 'published',
} as const

interface WorkflowUser {
  imageUrl: string
  name: string
  id: number
}

interface SimpleWorkflow {
  phases: Array<Phase>
}
interface Loaders {
  startingWorkflow: boolean
}

type GetWorkflowArgs = {
  contentAccountId: number
  identifier: string | number
  showLoader: boolean
  type: string
}

export interface WorkflowPopoverProps {
  approvalStatuses: Array<string>
  attachmentId: string | number
  content: Content
  contentAccountId: number
  createApprovalItem: () => void
  createIntegrationTask: () => void
  createPhase: () => void
  createPhaseItem: () => void
  createWorkflow: () => void
  currentUser: WorkflowUser
  enableEdit: boolean
  getWorkflow: (args: GetWorkflowArgs) => void
  gotoWorkflow: () => void
  isLimitedRole: boolean
  loaders: Loaders
  onStartWorkflow: () => void
  resendApprovals: () => void
  resetWSLastFrame: () => void
  retractApprovals: () => void
  sendForApproval: () => void
  setLoader: () => void
  startWorkflow: () => void
  theme: 'light' | 'dark'
  updatePhase: () => void
  updatePhaseItem: () => void
  users: Array<User>
  workflow: SimpleWorkflow
  workflowLoaded: boolean
  workflowStarted: boolean
  workflowType: string
  workflowValid: boolean
}

export const WorkflowPopover = ({
  approvalStatuses,
  attachmentId,
  content,
  contentAccountId,
  createApprovalItem,
  createIntegrationTask,
  createPhase,
  createPhaseItem,
  createWorkflow,
  currentUser,
  enableEdit,
  getWorkflow,
  gotoWorkflow,
  isLimitedRole,
  loaders,
  onStartWorkflow,
  resendApprovals,
  resetWSLastFrame,
  retractApprovals,
  sendForApproval,
  setLoader,
  startWorkflow,
  theme,
  updatePhase,
  updatePhaseItem,
  users,
  workflow,
  workflowLoaded,
  workflowStarted,
  workflowType,
  workflowValid,
}: WorkflowPopoverProps) => {
  const [isPopoverOpen, setIsPopoverOpen] = useState(false)

  // `workflowLoaded` is one flag for every workflow in the store, so it flips
  // when any popover's workflow arrives. Fetch each workflow once, not again
  // for every other popover on the page.
  const requestedWorkflowKey = useRef<string | null>(null)
  useEffect(() => {
    const workflowKey = `${workflowType}/${attachmentId}/${contentAccountId}`
    if (requestedWorkflowKey.current === workflowKey) return

    if (!workflowLoaded || workflowStarted === undefined) {
      requestedWorkflowKey.current = workflowKey
      getWorkflow({
        contentAccountId,
        identifier: attachmentId,
        showLoader: false,
        type: workflowType,
      })
    }
  }, [
    attachmentId,
    contentAccountId,
    getWorkflow,
    workflowLoaded,
    workflowType,
    workflowStarted,
  ])

  const finalApprovalReached = isFinalApprovalReached(
    workflow ? workflow.phases : []
  )

  const approvalContent = getApprovalContent({
    content,
    phases: workflow ? workflow.phases : [],
    workflowStarted,
    finalApprovalReached,
  })

  const approvalStatus = workflowLoaded
    ? approvalContent.status
    : statusMap[approvalStatuses[0] as ContentApprovalStatus] || 'not-started'

  const { status: deliveryStatus } = getDeliveryContent(content, users)

  const onPopoverOpen = (open: boolean) => {
    if (open) {
      track('Workflow Popover: Open')
    }

    setIsPopoverOpen(open)
  }

  const navigateToWorkflow = () => {
    if (gotoWorkflow) {
      track('Workflow: Edit', {
        workflowPopover: true,
      })
      setIsPopoverOpen(false)
      gotoWorkflow()
    }
  }

  const shouldShowHeaderDivider =
    workflowLoaded &&
    workflowStarted &&
    !findFirstCompletePhase(workflow.phases)

  return (
    <ThemeProvider theme={theme}>
      <Popover.Root open={isPopoverOpen} onOpenChange={onPopoverOpen}>
        <Popover.Trigger
          tooltip={{
            content: 'Approval Status',
            // Tooltip appears in multiple different views, need to ensure
            // that it's always above other zIndex values when activated
            zIndex: 9999,
          }}
        >
          <Button
            className="workflowPopover-status-button"
            name="Content Approval Status"
            css={{ paddingInline: Spacing.S }}
            ariaLabel="Content Approval Status"
            variant="ghost"
          >
            <span style={{ display: 'flex', gap: '4px' }}>
              {workflowLoaded && workflowStarted !== undefined ? (
                <Fragment>
                  {approvalStatus && (
                    <WorkflowIcon status={approvalStatus} size={20} />
                  )}
                  <WorkflowIcon status={deliveryStatus} size={20} />
                </Fragment>
              ) : (
                <DotLoader />
              )}
            </span>
          </Button>
        </Popover.Trigger>
        <Popover.Content
          className="workflowPopover-content"
          css={{
            minWidth: 468,
            '> div': {
              backgroundColor: Color.light.Surface.N000,
              'span > svg': { fill: Color.light.Surface.N000 },
            },
          }}
          showArrow={true}
          side="left"
          // Popover appears in multiple different views, need to ensure
          // that it's always above other zIndex values when opened
          zIndex={9999}
        >
          <ThemeProvider theme="light">
            <div className={kuiperClassNames('workflowPopover')}>
              <div
                className={kuiperClassNames(
                  'workflowPopover__scrollableWrapper'
                )}
              >
                {workflowLoaded && (
                  <WorkflowPopoverHeader
                    onClickEdit={navigateToWorkflow}
                    shouldShowEdit={enableEdit}
                  />
                )}

                {shouldShowHeaderDivider && (
                  <hr
                    className={kuiperClassNames('workflowPopover__divider')}
                  />
                )}

                <PhaseList
                  attachmentId={attachmentId}
                  contentAccountId={contentAccountId}
                  createApprovalItem={createApprovalItem}
                  createIntegrationTask={createIntegrationTask}
                  createPhase={createPhase}
                  createPhaseItem={createPhaseItem}
                  createWorkflow={createWorkflow}
                  currentUser={currentUser}
                  getWorkflow={getWorkflow}
                  isLimitedRole={isLimitedRole}
                  loaders={loaders}
                  onStartWorkflow={onStartWorkflow}
                  resendApprovals={resendApprovals}
                  resetWSLastFrame={resetWSLastFrame}
                  retractApprovals={retractApprovals}
                  sendForApproval={sendForApproval}
                  setLoader={setLoader}
                  startWorkflow={startWorkflow}
                  updatePhase={updatePhase}
                  updatePhaseItem={updatePhaseItem}
                  workflow={workflow}
                  workflowLoaded={workflowLoaded}
                  workflowLocation={WorkflowLocation.Popover}
                  workflowParentType="content"
                  workflowStarted={workflowStarted}
                  workflowType={workflowType}
                  workflowValid={workflowValid}
                />
              </div>

              {workflowLoaded && (
                <hr className={kuiperClassNames('workflowPopover__divider')} />
              )}

              {workflowLoaded && (
                <WorkflowPopoverFooter
                  content={content}
                  finalApprovalReached={finalApprovalReached}
                  phases={workflow.phases}
                  users={users}
                  workflowStarted={workflowStarted}
                />
              )}

              <Loader loaded={workflowLoaded} theme="white" />
            </div>
          </ThemeProvider>
        </Popover.Content>
      </Popover.Root>
    </ThemeProvider>
  )
}
